import { HttpService } from "../http/http";
import { UserService } from "../../domain/user-service";
import { S3UploadFile } from "./s3-upload-file";
import { Logger } from "../logger";
import { Auth } from "../auth";
import {
  S3Bucket,
  TenantStorageOption,
} from "../../domain/tenant-storage-option";
import { environment } from "app/environments/environment";
import { ICloudStorageProvider } from "../../domain/Upload/i-cloud-storage-provider";
import { IUploadFile } from "../../domain/Upload/i-upload-file";
import { resolveSrv } from "dns";
import Evaporate from "evaporate";
import CryptoJS from "crypto-js";

export class S3CloudProvider implements ICloudStorageProvider {
  constructor(
    private httpService: HttpService,
    private userService: UserService,
    private logger: Logger,
    private auth: Auth,
  ) {}

  public protectedBucketName: string;
  public publicBucketName: string;
  public alternateRegionName: string = null;
  public alternateBucketName: string = null;
  private regionEndpoint: string;
  // private ssekmsMasterKeyId: string;
  private evaporate;
  private _evaporateForOriginalFiles;
  private _evaporateForTranscodedFiles;

  private initialized = false;
  private customServiceUrl: string = null;
  private cdnServiceUrl: string = null;
  private _originalMediaBucketDetails;
  private _transcodedFilesBucketDetails;

  init() {
    this.initialized = true;

    return this.httpService.get("s3/credentials").then((credentials) => {
      if (credentials.useCustomServiceUrl) {
        console.log("S3CloudProvider => Using the custom service url");

        this.customServiceUrl = credentials.ServiceUrl;
      }

      if (credentials.CdnServiceUrl) {
        console.log("S3CloudProvider => Using the a cdn service url");

        this.cdnServiceUrl = credentials.CdnServiceUrl;
      }

      this.regionEndpoint = credentials.RegionEndpoint;
      this.protectedBucketName = credentials.ProtectedBucketName;
      this.publicBucketName = credentials.PublicBucketName;
      this.alternateRegionName = credentials.AlternateRegionName;
      this.alternateBucketName = credentials.AlternateUrlBucketName;
      const serviceUrl = this.getS3RegionEndpoint(this.getRegionEndpoint());
      this.initEvaporateInstancePromise(
        credentials.AccessKeyId,
        this.protectedBucketName,
        this.getRegionEndpoint(),
        serviceUrl,
      ).then((e) => {
        this.evaporate = e;
        this.initialized = true;
      });

      this._originalMediaBucketDetails = credentials.OriginalFilesCredentials;
      this.initEvaporateInstancePromise(
        credentials.OriginalFilesCredentials.AccessKey,
        credentials.OriginalFilesCredentials.BucketName,
        credentials.OriginalFilesCredentials.RegionEndpoint,
        credentials.OriginalFilesCredentials.ServiceUrl,
      ).then((e) => {
        this._evaporateForOriginalFiles = e;
        this.initialized = true;
      });

      this._transcodedFilesBucketDetails =
        credentials.TranscodedFilesCredentials;
      if (this._transcodedFilesBucketDetails) {
        this.initEvaporateInstancePromise(
          credentials.TranscodedFilesCredentials.AccessKey,
          credentials.TranscodedFilesCredentials.BucketName,
          credentials.TranscodedFilesCredentials.RegionEndpoint,
          credentials.TranscodedFilesCredentials.ServiceUrl,
        ).then((e) => {
          this._evaporateForTranscodedFiles = e;
          this.initialized = true;
        });
      }
    });
  }

  getS3RegionEndpoint(regionName) {
    if (this.customServiceUrl) {
      return this.customServiceUrl;
    }

    if (regionName === "us-east-1") {
      return "s3.amazonaws.com";
    }
    return `https://s3-${regionName}.amazonaws.com`;
  }

  isCustomServiceUrl() {
    return this.customServiceUrl && this.customServiceUrl.length > 0;
  }

  getUltimatePartSize() {
    return 72 * 1024 * 1024;
  }

  // tslint:disable-next-line:max-line-length
  getBucketName(
    uploadType:
      | "originalMediaContent"
      | "mediaContent"
      | "thumbnail"
      | "graphics"
      | "graphicsThumbnails"
      | "graphicsFont"
      | "graphicsSampleVideo"
      | "videoLogo",
  ): string {
    switch (uploadType) {
      case "thumbnail":
      case "graphicsThumbnails":
        return this.publicBucketName;
      case "mediaContent":
      case "graphics":
      case "graphicsFont":
      case "videoLogo":
      case "graphicsSampleVideo":
        return this.protectedBucketName;
    }
  }
  createOriginalMediaFile(
    id: string,
    file: File,
    fileName: string,
    notifyServer: (awsKey: string) => Promise<any>,
    customData?: any,
  ): IUploadFile {
    return new S3UploadFile(
      id,
      file,
      fileName,
      notifyServer,
      customData,
      this._evaporateForOriginalFiles,
      this._originalMediaBucketDetails.TempFilesBucketName ||
        this._originalMediaBucketDetails.BucketName,
      this.logger,
      this.cdnServiceUrl,
      new Date(),
      this.getS3RegionEndpoint(this.regionEndpoint),
      this,
      "original_file_bucket",
      null,
    );
  }
  createTranscodedFile(
    id: string,
    file: File,
    fileName: string,
    notifyServer: (awsKey: string) => Promise<any>,
    customData?: any,
  ): IUploadFile {
    return new S3UploadFile(
      id,
      file,
      fileName,
      notifyServer,
      customData,
      this._evaporateForTranscodedFiles,
      this._transcodedFilesBucketDetails.BucketName,
      this.logger,
      this.cdnServiceUrl,
      new Date(),
      this.getS3RegionEndpoint(this.regionEndpoint),
      this,
      "transcoded_files_bucket",
      null,
    );
  }
  createFile(
    id: string,
    file: File,
    fileName: string,
    uploadType:
      | "originalMediaContent"
      | "mediaContent"
      | "thumbnail"
      | "graphics"
      | "graphicsThumbnails"
      | "graphicsFont"
      | "graphicsSampleVideo",
    notifyServer: (awsKey: string) => Promise<any>,
    customData?: any,
  ): IUploadFile {
    return new S3UploadFile(
      id,
      file,
      fileName,
      notifyServer,
      customData,
      this.evaporate,
      this.getBucketName(uploadType),
      this.logger,
      this.cdnServiceUrl,
      new Date(),
      this.getS3RegionEndpoint(this.regionEndpoint),
      this,
      null,
      null,
    );
  }

  async createFileForTenantBucket(
    id: string,
    file: File,
    s3Filename: string,
    tenantStorageOption: TenantStorageOption,
  ): Promise<IUploadFile> {
    const s3Bucket = tenantStorageOption as S3Bucket;

    let evaporate = this.evaporate;
    if (!evaporate) {
      const serviceUrl = this.getS3RegionEndpoint(s3Bucket.region);
      evaporate = await this.initEvaporateInstancePromise(
        s3Bucket.accessKey,
        s3Bucket.bucketName,
        s3Bucket.region,
        serviceUrl,
      );
    }

    return new S3UploadFile(
      id,
      file,
      s3Filename,
      () => {
        return Promise.resolve();
      },
      undefined,
      evaporate,
      s3Bucket.bucketName,
      this.logger,
      this.cdnServiceUrl,
      new Date(),
      this.getS3RegionEndpoint(s3Bucket.region),
      this,
      s3Bucket.id,
      s3Bucket.accessKey,
    );
  }

  getRegionEndpoint() {
    return this.regionEndpoint;
  }

  private initEvaporateInstancePromise(
    accesKeyId: string,
    bucketName: string,
    region: string,
    serviceUrl: string,
  ) {
    return Evaporate.create({
      awsSignatureVersion: "4",
      aws_key: accesKeyId,
      bucket: bucketName, // The AWS 'bucket' option must be present
      computeContentMd5: true,
      cryptoMd5Method: function (data) {
        return CryptoJS.MD5(CryptoJS.lib.WordArray.create(data)).toString(
          CryptoJS.enc.Base64,
        );
      },
      cryptoHexEncodedHash256: function (data) {
        const input =
          typeof data === "string" ? data : CryptoJS.lib.WordArray.create(data);
        return CryptoJS.SHA256(input).toString(CryptoJS.enc.Hex);
      },
      aws_url: serviceUrl,
      signerUrl: environment.ENDPOINT + "/api/s3/signv4",
      signHeaders: {
        Authorization: () => {
          return this.auth.token.tokenType + " " + this.auth.token.token;
        },
      },
      signResponseHandler: function (
        response,
        stringToSign,
        signatureDateTime,
      ) {
        let responseJson;
        try {
          responseJson = JSON.parse(response);
        } catch (err) {
          return Promise.reject("error parsing response: " + response);
        }
        if (responseJson && responseJson.message) {
          return Promise.resolve(responseJson.message);
        }
        return Promise.reject("invalid response from signer");
      },
      awsRegion: region,
      logging: false,
      partSize: this.getUltimatePartSize(),
      maxConcurrentParts: 2,
    });
  }
}
